import {SPECIAL_BUILDING_TOKENS} from "../../../../constants/tokens";
import {INCOME_INFLUENCE_PATH_LENGTH} from "../../../../configs/income";
import {
  selectHexes,
  selectPlayers,
  selectSpecialBuildings
} from "../../../../redux/reducers/sharedStateReducer/selectors";
import {selectConstructionPlanEntries} from "../../../../redux/reducers/privateStateReducer/selectors";
import {getHexDistance} from "../../../coords/getHexDistance";
import {calculateCitiesCache} from "../../calculateCitiesCache";
import {getGuildsAndTemplesWithChangedIncome} from "../../income/getGuildsAndTemplesWithChangedIncome";
import {checkIfConstructionPlanningAvailable, getConstructionPlanPlayer} from "./getConstructionPlanContext";
import {applyConstructionPlanEntriesToField} from "./buildConstructionPlanningViewState";

export const INCOME_SPECIAL_BUILDING_TOKENS = [SPECIAL_BUILDING_TOKENS.GUILD, SPECIAL_BUILDING_TOKENS.TEMPLE];

// Ячейки, где поля различаются
export const collectChangedHexKeys = (hexesBefore, hexesAfter) => {
  const changed = new Set();
  [hexesBefore, hexesAfter].forEach((hexes) => {
    Object.keys(hexes).forEach((hexKey) => {
      if (hexesBefore[hexKey] !== hexesAfter[hexKey]) {
        changed.add(hexKey);
      }
    });
  });
  return [...changed];
};

/**
 * Запланированные гильдии и храмы, чей доход изменила чужая стройка на
 * placedHexKey, — у планирующего игрока анимация дохода должна играть и на
 * них, как на построенных (см. playNearbyGuildsTemplesIncomeChangeAnimation).
 *
 * Доход сравнивается на поле «с планом»: пути влияния идут и через
 * запланированное.
 *  - «До» — поле и план такими, какими игрок видел их до чужой стройки
 *    (entriesBefore). Та же стройка могла снять часть плана — на своей ячейке
 *    или кварталом поблизости (constructionPlanMiddleware), и снятое звено
 *    пути тоже меняет доход оставшейся гильдии.
 *  - «После» — текущее поле и план, уже без снятого.
 *  - Перебираются только гильдии и храмы, оставшиеся в плане: снятые
 *    анимировать незачем.
 * Без entriesBefore «до» строится по текущему плану.
 *
 * Вне планирования (не чужой ход фазы строительства) и без запланированных
 * гильдий/храмов — null.
 *
 * @returns {null | {
 *   affected: Array<{hexKey: string, buildingType: string}>,
 *   player: Object,
 *   hexes: Object,
 *   specialBuildings: Object,
 *   citiesCache: Object,
 * }} affected и поле «с планом» после стройки — для самой анимации
 */
export const getPlannedGuildsAndTemplesIncomeChange = (state, placedHexKey, hexesBefore, entriesBefore) => {
  if (!checkIfConstructionPlanningAvailable(state) || !hexesBefore) {
    return null;
  }

  const entries = selectConstructionPlanEntries(state);
  const plannedIncomeBuildings = Object.fromEntries(entries
    .filter(({token}) => INCOME_SPECIAL_BUILDING_TOKENS.includes(token))
    .map(({hexKey, token}) => [hexKey, token]));
  if (Object.keys(plannedIncomeBuildings).length === 0) {
    return null;
  }

  const specialBuildings = selectSpecialBuildings(state);
  const fieldBefore = applyConstructionPlanEntriesToField(hexesBefore, specialBuildings, entriesBefore || entries);
  const fieldAfter = applyConstructionPlanEntriesToField(selectHexes(state), specialBuildings, entries);
  const changedHexKeys = collectChangedHexKeys(fieldBefore.hexes, fieldAfter.hexes);

  const result = {
    affected: [],
    player: getConstructionPlanPlayer(state),
    hexes: fieldAfter.hexes,
    specialBuildings: fieldAfter.specialBuildings,
    citiesCache: null,
  };

  // Ранний выход до пересчета городов (полный обход поля): ни одна
  // запланированная гильдия не дотягивается до изменившихся ячеек
  const isAnyWithinReach = Object.keys(plannedIncomeBuildings).some((hexKey) => (
    changedHexKeys.some((changedHexKey) => getHexDistance(changedHexKey, hexKey) <= INCOME_INFLUENCE_PATH_LENGTH)
  ));
  if (!isAnyWithinReach) {
    return result;
  }

  result.citiesCache = calculateCitiesCache(fieldAfter.hexes, fieldAfter.specialBuildings);
  // Перебираются только запланированные гильдии и храмы: построенные уже
  // анимирует общий расчет по настоящему полю
  result.affected = getGuildsAndTemplesWithChangedIncome(
    placedHexKey,
    fieldBefore.hexes,
    fieldAfter.hexes,
    plannedIncomeBuildings,
    selectPlayers(state),
    calculateCitiesCache(fieldBefore.hexes, fieldBefore.specialBuildings),
    result.citiesCache,
    {changedHexKeys},
  );
  return result;
};
